import React from 'react';
import { Box, Link, Typography } from '@mui/material';

const extractableExtensions = new Set(['pdf', 'docx', 'png', 'jpg', 'jpeg', 'heic', 'webp']);

const getExtension = (name = '') => String(name).toLowerCase().split('.').pop();

const getAttachmentIdentity = (attachment = {}, index) => (
  attachment.storagePath || attachment.path || attachment.id || attachment.url || `attachment-${index}`
);

export const getVisibleChatAttachments = (attachments = []) => {
  const seen = new Set();
  return attachments.filter((attachment, index) => {
    const identity = getAttachmentIdentity(attachment, index);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

const getTypeLabel = (attachment = {}, name = '') => {
  const extension = getExtension(name);
  if (extension && extension !== name.toLowerCase()) return extension.toUpperCase();
  const mimeSubtype = String(attachment.mimeType || '').split('/')[1];
  return mimeSubtype ? mimeSubtype.toUpperCase() : 'FILE';
};

const MessageAttachments = ({ attachments = [] }) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;

  return (
    <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
      {getVisibleChatAttachments(attachments).map((attachment, index) => {
        const name = attachment.filename || attachment.name || 'Attachment';
        const key = attachment.id || attachment.storagePath || attachment.path || `${name}-${index}`;
        const isImage = attachment.type === 'image' || String(attachment.mimeType || '').startsWith('image/');
        if (isImage && attachment.url) {
          return (
            <Box key={key}>
              <Link href={attachment.url} target="_blank" rel="noopener noreferrer" aria-label={`Open image ${name}`} sx={{ display: 'block' }}>
                <Box
                  component="img"
                  src={attachment.url}
                  alt={name}
                  sx={{ display: 'block', width: '100%', maxWidth: 420, maxHeight: 280, borderRadius: 2, objectFit: 'contain', bgcolor: 'rgba(255,255,255,0.55)' }}
                />
              </Link>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Shared in chat · Image
              </Typography>
            </Box>
          );
        }
        const typeLabel = getTypeLabel(attachment, name);
        return (
          <Box key={key} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.55)', minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800 }}>
              {typeLabel} · Shared in chat
            </Typography>
            {attachment.url ? (
              <Link href={attachment.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ display: 'block', overflowWrap: 'anywhere', fontWeight: 700 }}>
                {name}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere', fontWeight: 700 }}>{name}</Typography>
            )}
            {!extractableExtensions.has(getExtension(name)) && (
              <Typography variant="caption" display="block" color="text.secondary">Saved in chat; Lifelog processing is not available for this file type.</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default MessageAttachments;
